import { isAlpharunEmail } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import type { OAuthAuthorizationDetails } from "@supabase/supabase-js";
import { type FC, useEffect, useState } from "react";
import { z } from "zod";
import { getSupabaseClient } from "../../api/supabaseClient.js";
import {
  FullScreenLoader,
  FullScreenMessage,
} from "../../app/appStatusScreens.js";

// Blue wordmark on our own CDN (the consent card uses a light `bg-background`).
// Same asset HQ renders (apps/hq/src/helpers/logoUrls.ts); admin can't import
// from the hq app, so the URL is repeated here.
const ALPHARUN_LOGO_URL = "https://assets.alpharun.com/alpharun.png";

// User-facing labels for the standard OIDC scopes Supabase's OAuth server
// issues. The consent card receives the raw scope tokens (`details.scope`); any
// token not listed here (e.g. a future custom scope) falls back to the raw
// value so it's still shown rather than dropped.
const SCOPE_LABELS: Record<string, string> = {
  openid: "Confirm your identity",
  email: "Your email address",
  profile: "Your profile (name, picture)",
  phone: "Your phone number",
};

function scopeLabel(scope: string): string {
  return SCOPE_LABELS[scope] ?? scope;
}

/** Where Supabase sends the browser to approve an MCP client (its OAuth authorization URL). */
const MCP_OAUTH_CONSENT_PATH = "/mcp/oauth/authorize";

/** Whether the tab is on the MCP OAuth consent page. */
export function checkIsMcpOAuthConsentPage(): boolean {
  return window.location.pathname === MCP_OAUTH_CONSENT_PATH;
}

interface McpOAuthConsentProps {
  /** Display name of the MCP client requesting access. */
  readonly clientName: string;
  /** Email of the signed-in user being asked to consent, if known. */
  readonly userEmail: string | null;
  /**
   * Whether the user is an employee who gets service access over MCP rather
   * than their own account.
   */
  readonly isServiceAccess: boolean;
  /** The host approving sends the browser and the code to, if known. */
  readonly redirectHost: string | null;
  /** Human-readable scopes the client is requesting. */
  readonly scopes: readonly string[];
  /** Inline error to show above the actions (e.g. a failed approve/deny). */
  readonly errorMessage: string | null;
  /** Disables the buttons + spins Approve while a decision is in flight. */
  readonly isSubmitting: boolean;
  readonly onApprove: () => void;
  readonly onDeny: () => void;
}

/**
 * Presentational consent card. Pure props-in so it renders standalone in
 * Storybook (the {@link McpOAuthConsentPage} container owns the data fetching
 * and Supabase calls).
 */
export const McpOAuthConsent: FC<McpOAuthConsentProps> = ({
  clientName,
  userEmail,
  isServiceAccess,
  redirectHost,
  scopes,
  errorMessage,
  isSubmitting,
  onApprove,
  onDeny,
}) => {
  return (
    <div className="flex h-screen flex-col items-center justify-center px-4">
      <div className="border-border w-full max-w-md rounded-xl border bg-background p-8 shadow-sm">
        <img
          src={ALPHARUN_LOGO_URL}
          alt="Alpharun"
          className="mb-6 h-6 w-auto"
        />
        <h1 className="text-xl font-semibold text-foreground">
          Authorize {clientName}
        </h1>
        {isServiceAccess ? (
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium">{clientName}</span> is requesting
            Alpharun service access as you
            {userEmail ? ` (${userEmail})` : ""}. This doesn&apos;t open any
            customer&apos;s data yet: for each customer, you&apos;ll start a
            session in HQ.
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium">{clientName}</span> is requesting
            access to your Alpharun account
            {userEmail ? ` (${userEmail})` : ""}.
          </p>
        )}

        {redirectHost ? (
          <p className="mt-2 text-sm text-muted-foreground">
            You&apos;ll be returned to{" "}
            <span className="font-medium">{redirectHost}</span>.
          </p>
        ) : null}

        {scopes.length > 0 ? (
          <div className="mt-4">
            <p className="text-sm font-medium text-foreground">
              Requested access
            </p>
            <ul className="mt-2 list-disc pl-5 text-sm text-muted-foreground">
              {scopes.map((scope) => (
                <li key={scope}>{scopeLabel(scope)}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {errorMessage ? (
          <p className="mt-4 text-sm text-red-600">{errorMessage}</p>
        ) : null}

        <div className="mt-6 flex justify-end gap-3">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={onDeny}
            isDisabled={isSubmitting}
          >
            Deny
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={onApprove}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
          >
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Supabase's authorization details carry the request's `redirect_uri`, which
 * its client types leave out.
 */
const redirectUriSchema = z.object({ redirect_uri: z.string() });

/** The host of the request's redirect URI, where approving sends the code. */
function parseRedirectHost(details: OAuthAuthorizationDetails): string | null {
  const parsed = redirectUriSchema.safeParse(details);
  if (!parsed.success || !URL.canParse(parsed.data.redirect_uri)) {
    return null;
  }
  return new URL(parsed.data.redirect_uri).host;
}

function parseScopes(scope: string): string[] {
  return scope
    .split(" ")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/**
 * MCP OAuth consent screen.
 *
 * Supabase (the OAuth 2.1 authorization server) redirects the browser here —
 * `{SITE_URL}/mcp/oauth/authorize?authorization_id=...` — mid-flow when an MCP
 * client requests access. The logged-in user's Supabase session authorizes the
 * `auth.oauth.*` calls; on approve/deny Supabase returns a `redirect_url` that
 * sends the browser back to the MCP client.
 *
 * Rendered by the app shell (`App`): for an alpharun.com session as soon as it
 * exists, since an employee in service mode may have no teammate of their own;
 * for everyone else once account resolution finds an active teammate.
 */
export const McpOAuthConsentPage: FC = () => {
  const authorizationId = new URLSearchParams(window.location.search).get(
    "authorization_id",
  );

  const [details, setDetails] = useState<OAuthAuthorizationDetails | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!authorizationId) {
      setErrorMessage("This authorization link is missing or invalid.");
      return;
    }

    let cancelled = false;
    void (async () => {
      const { data, error } = await getSupabaseClient()
        .auth.oauth.getAuthorizationDetails(authorizationId)
        .catch((caught: unknown) => ({
          data: null,
          error: caught as Error,
        }));

      if (cancelled) {
        return;
      }

      if (error || !data) {
        setErrorMessage(
          "We couldn't load this authorization request. It may have expired.",
        );
        return;
      }

      // A `redirect_url` here means consent was already granted previously —
      // send the user straight back to the client.
      if (data.redirect_url) {
        window.location.assign(data.redirect_url);
        return;
      }

      setDetails(data);
    })();

    return () => {
      cancelled = true;
    };
  }, [authorizationId]);

  async function decide(decision: "approve" | "deny") {
    if (!authorizationId) {
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(null);

    const oauth = getSupabaseClient().auth.oauth;
    // Skip the SDK's automatic browser redirect so we can fall back to a clear
    // error if the response carries no redirect URL.
    const { data, error } = await (
      decision === "approve"
        ? oauth.approveAuthorization(authorizationId, {
            skipBrowserRedirect: true,
          })
        : oauth.denyAuthorization(authorizationId, {
            skipBrowserRedirect: true,
          })
    ).catch((caught: unknown) => ({ data: null, error: caught as Error }));

    if (error || !data?.redirect_url) {
      setIsSubmitting(false);
      setErrorMessage("Something went wrong. Please try again.");
      return;
    }

    window.location.assign(data.redirect_url);
  }

  if (errorMessage && !details) {
    return <FullScreenMessage>{errorMessage}</FullScreenMessage>;
  }

  if (!details) {
    return <FullScreenLoader />;
  }

  return (
    <McpOAuthConsent
      clientName={details.client.name}
      userEmail={details.user.email}
      // The employee list stays out of this bundle, so any alpharun.com
      // account sees the service wording; `/mcp` decides the actual mode.
      isServiceAccess={isAlpharunEmail(details.user.email)}
      redirectHost={parseRedirectHost(details)}
      scopes={parseScopes(details.scope)}
      errorMessage={errorMessage}
      isSubmitting={isSubmitting}
      onApprove={() => void decide("approve")}
      onDeny={() => void decide("deny")}
    />
  );
};
